import type { RootState } from 'Data/Store/Store';
import type {
  AddOrIncrementResult,
  ICartItemData,
  IDeleteOrderedItemsFromBackendParams,
  IHandleAddItemParams,
  IHandleAddToCartParams,
  IHandleAddToCartResult,
  IHandleDeleteItemParams,
  IHandleGetCartParams,
  IHandleReconcileGuestParams,
  IHandleUpdateQuantityParams,
  ServerGetCartResponse,
} from '@model/Cart.model';
import { selectCartItems } from 'Data/Cart/CartSelectors';
import { AxiosCalls } from './Functions/AxiosCalls';
import { handleDefaultApiErrors } from 'Utils/Global/apiErrorHandling';
import type { Dispatch, SetStateAction } from 'react';
import { OrderItemSummary } from '@model/Order.model';

function mapServerCartItems(resp: ServerGetCartResponse): ICartItemData[] {
  const serverItems = resp?.data?.items ?? [];
  return serverItems.map((serverItem) => {
    const unitPrice = Number(serverItem?.product?.price ?? 0);
    const quantity = Math.max(0, Number(serverItem?.quantity ?? 0));
    return {
      id: serverItem?.product?.id ?? '',
      name: serverItem?.product?.title ?? '',
      imageUrl: serverItem?.product?.imageUrl ?? '',
      description: serverItem?.product?.shortDescription ?? '',
      price: unitPrice,
      qty: quantity,
      lineTotal: unitPrice * quantity,
      stock: serverItem?.product?.stock ?? 0,
      maxPerUser: serverItem?.product?.maxPerUser,
      currency: 'USD',
      cartItemId: serverItem?.cartItemId,
    };
  });
}

export class CartService {
  async handleGetCart({
    token,
    setLoading,
    setApiError,
    setItems,
    onAuthError,
  }: IHandleGetCartParams): Promise<void> {
    setLoading(true);
    setApiError(null);
    try {
      const response = await AxiosCalls.Get<ServerGetCartResponse>('/cart/getcart', token);
      const mappedItems = mapServerCartItems(response?.data ?? ({} as ServerGetCartResponse));
      setItems(mappedItems);
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
      });
      setApiError(msg);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  async handleAddItem({
    token,
    productId,
    quantity,
    setLoading,
    setApiError,
    setItems,
    onAuthError,
  }: IHandleAddItemParams): Promise<void> {
    setLoading(true);
    setApiError(null);
    try {
      await AxiosCalls.Post<void, { productId: string; quantity: number }>(
        '/cart/add',
        { productId, quantity },
        token
      );

      if (setItems) {
        await this.handleGetCart({ token, setLoading, setApiError, setItems, onAuthError });
      }

      window.dispatchEvent(new CustomEvent('cart:changed'));
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
      });
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  }

  async handleUpdateQuantity({
    token,
    cartItemId,
    quantity,
    setLoading,
    setApiError,
    setItems,
    onAuthError,
  }: IHandleUpdateQuantityParams): Promise<void> {
    setLoading(true);
    setApiError(null);
    try {
      await AxiosCalls.Put<void, { quantity: number }>(
        `/cart/items/quantity/${cartItemId}`,
        { quantity },
        token
      );

      if (setItems) {
        await this.handleGetCart({ token, setLoading, setApiError, setItems, onAuthError });
      }

      window.dispatchEvent(new CustomEvent('cart:changed'));
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
      });
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  }

  async handleReconcileGuestCart({
    token,
    guestItems,
    setLoading,
    setApiError,
    setItems,
    onAuthError,
  }: IHandleReconcileGuestParams): Promise<void> {
    setLoading(true);
    setApiError(null);

    let serverMappedItems: ICartItemData[] = [];

    const captureItems: Dispatch<SetStateAction<ICartItemData[]>> = (next) => {
      serverMappedItems =
        typeof next === 'function'
          ? (next as (prev: ICartItemData[]) => ICartItemData[])(serverMappedItems)
          : next;
    };

    const ignoreBool: Dispatch<SetStateAction<boolean>> = () => {};
    const ignoreError: Dispatch<SetStateAction<string | null>> = () => {};

    try {
      await this.handleGetCart({
        token,
        setLoading: ignoreBool,
        setApiError: ignoreError,
        setItems: captureItems,
        onAuthError,
      });

      const serverIndexByProductId = new Map<
        string,
        {
          cartItemId?: string;
          qty: number;
          stock?: number;
          maxPerUser?: number;
        }
      >();

      for (const item of serverMappedItems) {
        const availableStock = typeof item.stock === 'number' ? item.stock : undefined;
        const parsedMaxPerUser = Number(item.maxPerUser);
        const perUserLimit =
          Number.isFinite(parsedMaxPerUser) && parsedMaxPerUser > 0 ? parsedMaxPerUser : undefined;

        serverIndexByProductId.set(item.id, {
          cartItemId: item.cartItemId,
          qty: Math.max(0, Number(item.qty || 0)),
          stock: availableStock,
          maxPerUser: perUserLimit,
        });
      }

      for (const guestItem of guestItems) {
        const productId = guestItem.id;
        const guestQuantity = Math.max(0, Number(guestItem.qty || 0));
        const serverMatch = serverIndexByProductId.get(productId);

        if (serverMatch && serverMatch.cartItemId) {
          const combinedQty = serverMatch.qty + guestQuantity;

          const stockCap =
            typeof serverMatch.stock === 'number' && serverMatch.stock >= 0
              ? serverMatch.stock
              : Number.POSITIVE_INFINITY;

          const perUserCap =
            typeof serverMatch.maxPerUser === 'number' && serverMatch.maxPerUser > 0
              ? serverMatch.maxPerUser
              : Number.POSITIVE_INFINITY;

          const hardCap = Math.min(stockCap, perUserCap);
          const newQuantity = Number.isFinite(hardCap)
            ? Math.min(combinedQty, hardCap)
            : combinedQty;

          await this.handleUpdateQuantity({
            token,
            cartItemId: serverMatch.cartItemId,
            quantity: newQuantity,
            setLoading: ignoreBool,
            setApiError,
            onAuthError,
          });
        } else {
          const guestStockCap =
            typeof guestItem.stock === 'number' ? guestItem.stock : Number.POSITIVE_INFINITY;

          const guestPerUserCap =
            typeof guestItem.maxPerUser === 'number' && guestItem.maxPerUser > 0
              ? guestItem.maxPerUser
              : Number.POSITIVE_INFINITY;

          const hardCap = Math.min(guestStockCap, guestPerUserCap);
          const quantityToAdd = Number.isFinite(hardCap)
            ? Math.min(guestQuantity, hardCap)
            : guestQuantity;

          if (quantityToAdd > 0) {
            await this.handleAddItem({
              token,
              productId,
              quantity: quantityToAdd,
              setLoading: ignoreBool,
              setApiError,
              onAuthError,
            });
          }
        }
      }

      await this.handleGetCart({ token, setLoading, setApiError, setItems, onAuthError });
      window.dispatchEvent(new CustomEvent('cart:changed'));
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
      });
      setApiError(msg);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }
  async handleAddOrIncrement({
    token,
    productId,
    quantity,
    setLoading,
    setApiError,
    setItems,
    maxPerUserCap,
    stockCap,
    onAuthError,
  }: IHandleAddItemParams): Promise<AddOrIncrementResult> {
    const requestedQuantity = Math.max(0, Number(quantity || 0));
    setLoading(true);
    setApiError(null);

    try {
      const getResp = await AxiosCalls.Get<ServerGetCartResponse>('/cart/getcart', token);
      const serverItems = getResp?.data?.data?.items ?? [];

      const matchedItem = serverItems.find((s) => s?.product?.id === productId);
      const currentQuantity = Math.max(0, Number(matchedItem?.quantity ?? 0));

      const serverStock = Number(matchedItem?.product?.stock ?? NaN);
      const serverPerUser = Number(matchedItem?.product?.maxPerUser ?? NaN);

      const stockLimit =
        Number.isFinite(serverStock) && serverStock >= 0
          ? serverStock
          : typeof stockCap === 'number'
            ? Math.max(0, stockCap)
            : Infinity;

      const perUserLimit =
        Number.isFinite(serverPerUser) && serverPerUser > 0
          ? serverPerUser
          : typeof maxPerUserCap === 'number' && maxPerUserCap > 0
            ? maxPerUserCap
            : Infinity;

      const effectiveCap = Math.min(stockLimit, perUserLimit);
      const remainingCapacity = Number.isFinite(effectiveCap)
        ? Math.max(0, effectiveCap - currentQuantity)
        : Infinity;

      const appliedQuantity = Math.min(requestedQuantity, remainingCapacity);
      const wasConstrained = appliedQuantity < requestedQuantity;
      const resultingQuantity = currentQuantity + appliedQuantity;

      if (matchedItem?.cartItemId) {
        await this.handleUpdateQuantity({
          token,
          cartItemId: matchedItem.cartItemId,
          quantity: resultingQuantity,
          setLoading: () => {},
          setApiError,
          onAuthError,
        });
      } else if (appliedQuantity > 0) {
        await this.handleAddItem({
          token,
          productId,
          quantity: appliedQuantity,
          setLoading: () => {},
          setApiError,
          onAuthError,
        });
      }

      return { appliedQuantity, resultingQuantity, wasConstrained };
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
      });
      setApiError(msg);
      setItems?.([]);
      return {
        appliedQuantity: 0,
        resultingQuantity: 0,
        wasConstrained: false,
      };
    } finally {
      setLoading(false);
    }
  }

  async handleDeleteItem({
    token,
    cartItemId,
    setLoading,
    setApiError,
    setItems,
    onAuthError,
  }: IHandleDeleteItemParams): Promise<void> {
    setLoading(true);
    setApiError(null);
    try {
      await AxiosCalls.Delete<void>(`/cart/deletecartitem/${cartItemId}`, token);

      if (setItems) {
        await this.handleGetCart({ token, setLoading, setApiError, setItems, onAuthError });
      }

      window.dispatchEvent(new CustomEvent('cart:changed'));
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
      });
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  }

  async getCartItemsOnce(token: string): Promise<ICartItemData[]> {
    const resp = await AxiosCalls.Get<ServerGetCartResponse>('/cart/getcart', token);
    return mapServerCartItems(resp?.data ?? ({} as ServerGetCartResponse));
  }

  async getServerCartCount(token: string): Promise<number> {
    const items = await this.getCartItemsOnce(token);
    return items.reduce((n, it) => n + Math.max(0, Number(it.qty || 0)), 0);
  }
  async handleAddItemToCart({
    isLoggedIn,
    token,
    productId,
    requestQty,
    stockCap,
    perUserCap,
    remaining,
    stockRemaining,
    limitRemaining,
    onGuestAdd,
    setLoading,
    setApiError,
    setSuccessMsg,
    onAuthError,
  }: IHandleAddToCartParams & {
    setSuccessMsg?: (msg: string | null) => void;
  }): Promise<IHandleAddToCartResult> {
    const req = Math.max(0, Number(requestQty || 0));
    setLoading?.(true);
    setApiError?.(null);
    setSuccessMsg?.(null);

    try {
      if (!isLoggedIn) {
        const effRemaining = typeof remaining === 'number' ? Math.max(0, remaining) : 0;
        const canAdd = Math.min(req, effRemaining);

        if (canAdd <= 0) {
          const msg =
            typeof perUserCap === 'number' &&
            Number.isFinite(perUserCap) &&
            typeof limitRemaining === 'number' &&
            limitRemaining <= 0
              ? `You've reached the max ${perUserCap} per user.`
              : typeof stockRemaining === 'number' && stockRemaining <= 0
                ? '0 left — item is out of stock.'
                : "You've reached the maximum allowed for this item.";
          setApiError?.(msg);
          return { appliedQuantity: 0, wasConstrained: true, message: msg };
        }

        onGuestAdd?.(canAdd);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('cart:changed'));
        }

        const msg =
          canAdd < req
            ? typeof perUserCap === 'number' &&
              Number.isFinite(perUserCap) &&
              typeof limitRemaining === 'number' &&
              limitRemaining < (stockRemaining ?? Infinity)
              ? `Added the maximum allowed (${canAdd}). Max per user is ${perUserCap}.`
              : `Added the maximum allowed (${canAdd}).`
            : '';

        if (msg) setSuccessMsg?.(msg);
        return {
          appliedQuantity: canAdd,
          wasConstrained: canAdd < req,
          message: msg,
        };
      }

      const result = await this.handleAddOrIncrement({
        token: token ?? '',
        productId,
        quantity: req,
        setLoading: () => {},
        setApiError: () => {},
        onAuthError,
        stockCap: typeof stockCap === 'number' ? stockCap : undefined,
        maxPerUserCap: typeof perUserCap === 'number' && perUserCap > 0 ? perUserCap : undefined,
      });

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('cart:changed'));
      }

      const msg =
        result.appliedQuantity === 0
          ? typeof perUserCap === 'number'
            ? `You've reached the max ${perUserCap} per user.`
            : "You've reached the maximum allowed for this item."
          : result.wasConstrained
            ? typeof perUserCap === 'number'
              ? `Added the maximum allowed (${result.appliedQuantity}). Max per user is ${perUserCap}.`
              : `Added the maximum allowed (${result.appliedQuantity}).`
            : '';

      if (msg) setSuccessMsg?.(msg);
      return {
        appliedQuantity: result.appliedQuantity,
        wasConstrained: result.wasConstrained,
        message: msg,
      };
    } catch (e) {
      const msg = handleDefaultApiErrors(e as string | null, {
        hadToken: Boolean(token),
        onAuthError,
      });
      setApiError?.(msg);

      return { appliedQuantity: 0, wasConstrained: false, message: msg };
    } finally {
      setLoading?.(false);
    }
  }

  subscribeCartCount(params: { token: string; onChange: (count: number) => void }): () => void {
    let alive = true;
    const refresh = async () => {
      if (!params.token?.trim()) return;
      try {
        const total = await this.getServerCartCount(params.token);
        if (alive) params.onChange(total);
      } catch {}
    };

    void refresh();
    const handler = () => void refresh();
    if (typeof window !== 'undefined') {
      window.addEventListener('cart:changed', handler);
    }

    return () => {
      alive = false;
      if (typeof window !== 'undefined') {
        window.removeEventListener('cart:changed', handler);
      }
    };
  }

  mapCartStateToICartItems(state: RootState, currencyFallback = 'USD'): ICartItemData[] {
    const cartStateItems = selectCartItems(state);

    return cartStateItems.map((stateItem) => {
      const quantity = Math.max(0, Number(stateItem.quantity || 0));
      const unitPrice = Number(stateItem.price || 0);
      const max = Math.max(0, Number(stateItem.maxPerUser || 0));
      return {
        id: stateItem.id,
        name: stateItem.name,
        price: unitPrice,
        currency: currencyFallback,
        imageUrl: stateItem.imageUrl ?? '',
        qty: quantity,
        lineTotal: quantity * unitPrice,
        stock: stateItem.stock,
        maxPerUser: max,
      };
    });
  }

  computeCartSubtotalFromICartItems(items: ICartItemData[]): number {
    return items.reduce((sum, item) => sum + item.lineTotal, 0);
  }

  validateQuantity(
    nextQty: number,
    item: Pick<ICartItemData, 'stock' | 'maxPerUser'>
  ): { qty: number; error: string | null } {
    const qty = Math.max(0, Math.floor(nextQty || 0));
    if (typeof item.stock === 'number' && item.stock >= 0 && qty > item.stock) {
      return { qty, error: `Only ${item.stock} left in stock.` };
    }
    if (typeof item.maxPerUser === 'number' && item.maxPerUser > 0 && qty > item.maxPerUser) {
      return { qty, error: `Limit reached: max ${item.maxPerUser} per user.` };
    }
    return { qty, error: null };
  }

  async updateAccountQuantityWithValidation(params: {
    token: string;
    item: ICartItemData;
    onAuthError: (message: string) => void;
    nextQty: number;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setApiError: React.Dispatch<React.SetStateAction<string | null>>;
    setItems?: React.Dispatch<React.SetStateAction<ICartItemData[]>>;
  }): Promise<string | null> {
    const { qty, error } = this.validateQuantity(params.nextQty, params.item);
    if (error) return error;
    await this.handleUpdateQuantity({
      token: params.token,
      cartItemId: params.item.cartItemId!,
      quantity: qty,
      setLoading: params.setLoading,
      setApiError: params.setApiError,
      setItems: params.setItems,
      onAuthError: params.onAuthError,
    });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('cart:changed'));
    }
    return null;
  }

  updateGuestQuantityWithValidation(params: {
    item: ICartItemData;
    nextQty: number;
    changeQty: (id: string, qty: number) => void;
  }): string | null {
    const { qty, error } = this.validateQuantity(params.nextQty, params.item);
    if (error) return error;
    params.changeQty(params.item.id, qty);
    return null;
  }

  private mapSelectedCartItemsToOrderItems(params: {
    cartItems: ICartItemData[];
    selectedIds: Set<string>;
  }): OrderItemSummary[] {
    const { cartItems, selectedIds } = params;

    return (cartItems ?? [])
      .filter((it) => it?.id && selectedIds.has(it.id))
      .map((it) => {
        const quantity = Number(it.qty || 0);
        const unitPrice = Number(it.price || 0);
        const totalPrice = Number(it.lineTotal ?? unitPrice * quantity);

        return {
          id: String(it.id),
          cartItemId: it.cartItemId,
          name: String(it.name ?? ''),
          image: it.imageUrl,
          quantity,
          unitPrice,
          totalPrice,
        };
      });
  }

  handleProceedToCheckout(params: {
    cartItems: ICartItemData[];
    selectedIds: Set<string>;
    setOrderItems: (items: OrderItemSummary[]) => void;
    navigate: (path: string) => void;
  }): void {
    const { cartItems, selectedIds, setOrderItems, navigate } = params;

    const orderItems = this.mapSelectedCartItemsToOrderItems({
      cartItems,
      selectedIds,
    });

    setOrderItems(orderItems);
    navigate('/checkout');
  }

  async deleteOrderedItemsFromBackend(params: IDeleteOrderedItemsFromBackendParams): Promise<void> {
    const token = params.token?.trim() ?? '';
    if (!token) return;

    const ids = (params.orderItems ?? []).map((it) => String(it.cartItemId ?? '')).filter(Boolean);

    if (!ids.length) return;

    try {
      await AxiosCalls.DeleteWithData<void, { ids: string[] }>(
        '/cart/items/delete-items',
        { ids },
        token
      );

      const productIds = (params.orderItems ?? []).map((it) => String(it.id ?? '')).filter(Boolean);

      params.removeManyFromCart(productIds);

      window.dispatchEvent(new CustomEvent('cart:changed'));
    } catch (err) {
      handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError: params.onAuthError,
      });
    }
  }
}
